import * as React from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { EditInBulkPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'

type LocationStateType = {
  employeesIds?: number[]
  selectedLegalEntity?: string
} | null

const EditInBulkPage = () => {
  const {
    company,
    access: { locale, id: currentUser },
  } = useCurrentSession()
  const location = useLocation()
  const locationState: LocationStateType = location.state
  const selectedIds = new Set(locationState?.employeesIds ?? [])
  const selectedLegalEntityId = locationState?.selectedLegalEntity

  const nuvo_import_type = 'employee_edition'

  const { data, loading, error } = useQuery(EditInBulkPageDocument, {
    variables: {
      legalEntityId: Number(selectedLegalEntityId) || 0,
    },
  })

  const legalEntityCountry = data?.companies.legalEntitiesConnection.nodes[0]?.country
  const legalEntitiesIds = React.useMemo(
    () => (selectedLegalEntityId ? [Number(selectedLegalEntityId)] : []),
    [selectedLegalEntityId]
  )
  const metadata = {
    legal_entity_ids: legalEntitiesIds,
    employee_ids: Array.from(selectedIds),
    legal_entity_countries: legalEntityCountry ? [legalEntityCountry] : [],
  }

  const tracker_id = crypto.randomUUID()

  const commonEventProperties = useUnsafeMemo(
    () => ({
      user_id: currentUser,
      company_id: company.id,
      legal_entity_ids: legalEntitiesIds,
      import_type: nuvo_import_type,
      tracker_id: tracker_id,
    }),
    [currentUser, company.id, legalEntitiesIds, nuvo_import_type, tracker_id]
  )

  const canImportResources = data?.universalImporter._policies.importResources.can

  if (loading) {
    return <Loading />
  }

  track(trackingEvents.universalImporter.imports.reviewStep, commonEventProperties)

  return (
    <ResponsivePage.Body
      authorized={canImportResources}
      loading={loading}
      error={!selectedLegalEntityId || error}
      showHeader={false}
    >
      <NuvoImporter
        importType={nuvo_import_type}
        companyId={company.id}
        locale={locale}
        metadata={metadata}
        isEdition
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default EditInBulkPage
